import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  FaSearch,
  FaBars,
  FaTimes,
  FaUser,
  FaPhoneAlt,
  FaEnvelope,
} from "react-icons/fa";
import { User } from "lucide-react";
import { apiUrl, brands } from "../../../config/config";
import SearchBar from "./SearchBar";
import CartCount from "./CartCount";
import logo from "../../../assets/patchdesigner.png";

const Header = () => {
  const [open, setOpen] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [activeMenu, setActiveMenu] = useState(null);
  const [mobileOpenMenu, setMobileOpenMenu] = useState(null);

  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);

  const [user, setUser] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const desktopDropdownRef = useRef(null);
  const mobileDropdownRef = useRef(null);

  const mobileIconClass =
    "flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#e5e7eb] bg-white text-[#2b2b2b] transition hover:border-[#f97316] hover:text-[#f97316]";

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isDesktopInside =
        desktopDropdownRef.current &&
        desktopDropdownRef.current.contains(event.target);

      const isMobileInside =
        mobileDropdownRef.current &&
        mobileDropdownRef.current.contains(event.target);

      if (!isDesktopInside && !isMobileInside) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    const storedUser = localStorage.getItem("user");

    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);

        if (parsedUser?.id) {
          fetch(`${apiUrl}/api/users/${parsedUser.id}`)
            .then((res) => res.json())
            .then((data) => {
              if (isMounted) setUser(data);
            })
            .catch(() => {
              if (isMounted) setUser(parsedUser);
            });
        }
      } catch {
        localStorage.removeItem("user");
      }
    }

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    fetch(`${apiUrl}/api/${brands}/category`)
      .then((res) => res.json())
      .then((data) => {
        if (isMounted) setCategories(data.data || []);
      })
      .catch((err) => console.log("Category fetch error:", err));

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    fetch(`${apiUrl}/api/${brands}/products`)
      .then((res) => res.json())
      .then((data) => {
        if (isMounted) setProducts(data.data || []);
      })
      .catch((err) => console.log("Products fetch error:", err));

    return () => {
      isMounted = false;
    };
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("user");
    setUser(null);
    setDropdownOpen(false);
    window.location.href = "/login";
  };

  return (
    <>
      {/* MAIN HEADER */}
      <div className="sticky top-0 z-50 w-full border-t-[3px] border-[#111827] border-b border-[#e5e7eb] bg-white shadow-sm">
        <div className="mx-auto grid min-h-[88px] max-w-[1600px] grid-cols-[1fr_auto_1fr] items-center gap-3 px-4 py-2 sm:px-6 lg:min-h-[94px] lg:px-10">
          {/* LEFT SEARCH */}
          <div className="flex min-w-0 items-center justify-start gap-3">
            <button
              type="button"
              onClick={() => setOpen(true)}
              className={mobileIconClass + " lg:hidden"}
              aria-label="Open menu"
            >
              <FaBars className="h-[16px] w-[16px]" />
            </button>

            <div className="hidden w-full max-w-[310px] items-center lg:flex">
              <div className="flex h-[42px] w-full items-center rounded-full bg-white text-[#2b2b2b]">
                <FaSearch className="mr-3 h-[23px] w-[23px] shrink-0 text-[#555]" />

                <div className="w-full [&_button]:!hidden [&_input]:!h-[38px] [&_input]:!w-full [&_input]:!border-0 [&_input]:!bg-transparent [&_input]:!p-0 [&_input]:!text-[15px] [&_input]:!font-medium [&_input]:!text-[#2b2b2b] [&_input]:!outline-none [&_input]:placeholder:!text-[#2b2b2b]">
                  <SearchBar />
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setShowMobileSearch((prev) => !prev)}
              className={mobileIconClass + " lg:hidden"}
              aria-label="Search"
            >
              <FaSearch className="h-[16px] w-[16px]" />
            </button>
          </div>

          {/* CENTER LOGO */}
          <Link
            to="/"
            className="flex h-[74px] w-[122px] shrink-0 items-center justify-center overflow-hidden sm:h-[78px] sm:w-[140px] lg:h-[84px] lg:w-[165px]"
          >
            <img
              src={logo}
              alt="Logo"
              className="block max-h-full max-w-full object-contain"
            />
          </Link>

          {/* RIGHT ACTIONS */}
          <div className="flex min-w-0 items-center justify-end gap-3 sm:gap-5">
            <div className="hidden flex-col items-end gap-1 xl:flex">
              <span className="rounded-md bg-[#fff7ed] px-2 py-1.5 text-[12px] font-extrabold uppercase tracking-[0.7px] text-[#f97316]">
                Free Shipping Over $200
              </span>

              <div className="flex items-center gap-3 text-[12px] font-medium text-[#415164]">
                <span className="inline-flex items-center gap-1.5">
                  <FaPhoneAlt className="text-[12px] text-[#ed6504]" />
                  (703)-686-8193
                </span>

                <span className="hidden items-center gap-1.5 2xl:inline-flex">
                  <FaEnvelope className="text-[11px] text-[#f97316]" />
                  support@patchdesigners.com
                </span>
              </div>
            </div>

            <div className="relative" ref={desktopDropdownRef}>
              {user ? (
                <>
                  <button
                    type="button"
                    onClick={() => setDropdownOpen((prev) => !prev)}
                    className="hidden items-center gap-2 text-[#2b2b2b] transition hover:text-[#f97316] sm:flex"
                    aria-label="Account"
                  >
                    <FaUser className="h-[22px] w-[22px]" />
                    <span className="text-[15px] font-medium">Account</span>
                  </button>

                  {dropdownOpen && (
                    <div className="absolute right-0 z-50 mt-4 w-52 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl">
                      <p className="border-b border-gray-100 px-4 py-3 text-sm font-bold text-[#0a2336]">
                        {user.name}
                      </p>

                      <Link
                        to="/account"
                        className="block px-4 py-3 text-sm font-medium text-[#415164] transition hover:bg-[#fff7ed] hover:text-[#f97316]"
                        onClick={() => setDropdownOpen(false)}
                      >
                        My Account
                      </Link>

                      <button
                        onClick={handleLogout}
                        className="w-full px-4 py-3 text-left text-sm font-medium text-red-600 transition hover:bg-red-50"
                      >
                        Logout
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <Link
                  to="/login"
                  className="hidden items-center gap-2 text-[#2b2b2b] transition hover:text-[#f97316] sm:flex"
                >
                  <FaUser className="h-[22px] w-[22px]" />
                  <span className="text-[15px] font-medium">Account</span>
                </Link>
              )}
            </div>

            <div className="relative sm:hidden" ref={mobileDropdownRef}>
              <button
                type="button"
                onClick={() => setDropdownOpen((prev) => !prev)}
                className={mobileIconClass}
                aria-label="Account"
              >
                <User className="h-[20px] w-[20px]" />
              </button>

              {dropdownOpen && (
                <div className="absolute right-0 z-50 mt-4 w-48 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl">
                  {!user ? (
                    <Link
                      to="/login"
                      className="block px-4 py-3 text-sm font-medium text-[#415164] hover:bg-[#fff7ed] hover:text-[#f97316]"
                      onClick={() => setDropdownOpen(false)}
                    >
                      Sign In
                    </Link>
                  ) : (
                    <>
                      <p className="border-b border-gray-100 px-4 py-3 text-sm font-bold text-[#0a2336]">
                        {user.name}
                      </p>

                      <Link
                        to="/account"
                        className="block px-4 py-3 text-sm font-medium text-[#415164] hover:bg-[#fff7ed] hover:text-[#f97316]"
                        onClick={() => setDropdownOpen(false)}
                      >
                        My Account
                      </Link>

                      <button
                        onClick={handleLogout}
                        className="w-full px-4 py-3 text-left text-sm font-medium text-red-600 hover:bg-red-50"
                      >
                        Logout
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>

            <Link
              to="/cart"
              className="relative flex h-10 min-w-10 items-center justify-center gap-2 rounded-full border border-[#e5e7eb] px-2 text-[#2b2b2b] transition hover:border-[#f97316] hover:text-[#f97316] sm:h-auto sm:min-w-0 sm:border-0 sm:px-0"
              aria-label="Cart"
            >
              <CartCount />
              <span className="hidden text-[15px] font-medium sm:block">
                Cart
              </span>
            </Link>
          </div>
        </div>

        {/* MOBILE CONTACT STRIP */}
        <div className="border-t border-[#e5e7eb] bg-[#fff7ed] px-4 py-2 text-center xl:hidden">
          <div className="mx-auto flex max-w-[900px] flex-wrap items-center justify-center gap-x-4 gap-y-1 text-[11px] font-bold uppercase tracking-[0.4px] text-[#0a2336] sm:text-[12px]">
            <span className="text-[#f97316]">Free Shipping Over $200</span>
            <span className="normal-case text-[#415164]">(703)-686-8193</span>
            <span className="hidden normal-case text-[#415164] sm:inline">
              support@patchdesigners.com
            </span>
          </div>
        </div>

        {/* MOBILE SEARCH */}
        {showMobileSearch && (
          <div className="border-t border-[#e5e7eb] bg-white px-4 py-3 lg:hidden">
            <div className="rounded-full border border-[#e5e7eb] bg-[#f8fafc] px-4">
              <div className="flex h-[42px] items-center">
                <FaSearch className="mr-3 h-[15px] w-[15px] text-[#f97316]" />

                <div className="w-full [&_button]:!hidden [&_input]:!h-[40px] [&_input]:!w-full [&_input]:!border-0 [&_input]:!bg-transparent [&_input]:!p-0 [&_input]:!text-[14px] [&_input]:!font-medium [&_input]:!text-[#2b2b2b] [&_input]:!outline-none">
                  <SearchBar mobile />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* CATEGORY NAVIGATION */}
      <div
        className="sticky top-[126px] z-40 hidden w-full bg-[rgba(31,44,76,0.95)] text-white shadow-md lg:top-[130px] xl:top-[94px] lg:block"
        onMouseLeave={() => setActiveMenu(null)}
      >
        <ul className="mx-auto flex max-w-[1600px] items-center justify-center gap-x-3 whitespace-nowrap px-4 py-2 sm:px-6 lg:px-10 xl:gap-x-4 2xl:gap-x-5">
          {categories.map((cat) => (
            <li
              key={cat.id}
              className="group relative shrink-0 cursor-pointer"
              onMouseEnter={() => setActiveMenu(cat.id)}
            >
              <Link
                to={`/product/${cat.category_name.replace(/\s+/g, "-")}/${cat.id}`}
                className="relative inline-flex items-center rounded-md px-2 py-2 text-[clamp(13px,0.9vw,16px)] font-bold tracking-[0.2px] text-white transition-colors duration-200 hover:text-[#f97316] 2xl:text-[17px]"
              >
                {cat.category_name}
              </Link>

              {activeMenu === cat.id && (
                <div
                  className="absolute left-1/2 top-full z-50 w-max max-w-[calc(100vw-40px)] -translate-x-1/2 rounded-b-2xl border-t border-gray-200 bg-white px-5 py-6 shadow-2xl"
                  onMouseEnter={() => setActiveMenu(cat.id)}
                  onMouseLeave={() => setActiveMenu(null)}
                >
                  <div className="grid auto-cols-[210px] grid-flow-col gap-5">
                    {products
                      .filter((p) => p.category_id === cat.id)
                      .slice(0, 6)
                      .map((product) => (
                        <Link
                          key={product.id}
                          to={`/product/${cat.category_name.replace(
                            /\s+/g,
                            "-"
                          )}/${cat.id}/${product.name.replace(/\s+/g, "-")}/${
                            product.id
                          }`}
                          className="group/card mx-auto block h-full w-full max-w-[210px] overflow-hidden rounded-[15px] border border-[#d9d9d9] bg-[#f3f3f3] px-3 pb-4 pt-3 text-center shadow-[0_4px_16px_rgba(0,0,0,0.16)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_10px_26px_rgba(0,0,0,0.22)]"
                        >
                          <div className="mx-auto flex h-[132px] w-full items-center justify-center overflow-hidden rounded-[12px] bg-transparent">
                            <img
                              src={`${apiUrl}/storage/${product.image}`}
                              alt={product.name}
                              className="max-h-full max-w-full object-contain transition-transform duration-500 ease-out group-hover/card:scale-105"
                            />
                          </div>

                          <h3 className="mt-4 line-clamp-2 min-h-[38px] text-center text-[13px] font-extrabold leading-[19px] text-black">
                            {product.name}
                          </h3>

                          <p className="mx-auto mt-2 text-center text-[12px] font-medium leading-[20px] text-[#111827]">
                            As low as ${product.discountedPrice}
                          </p>
                        </Link>
                      ))}
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      </div>

      {/* MOBILE DRAWER */}
      {open && (
        <div className="fixed inset-0 z-50 bg-black/50 lg:hidden">
          <div className="fixed left-0 top-0 h-full w-[290px] overflow-y-auto bg-white p-5 shadow-2xl">
            <div className="mb-5 flex items-center justify-between border-b border-gray-100 pb-4">
              <h2 className="text-xl font-bold text-[#2b2b2b]">Menu</h2>

              <FaTimes
                className="cursor-pointer text-2xl text-[#2b2b2b] transition-colors duration-300 hover:text-[#f97316]"
                onClick={() => setOpen(false)}
              />
            </div>

            <nav className="mb-4">
              {!user ? (
                <Link
                  to="/login"
                  onClick={() => setOpen(false)}
                  className="flex items-center justify-center rounded-lg bg-[#0a2336] px-3 py-3 text-sm font-semibold text-white transition hover:bg-[#f97316]"
                >
                  Sign In
                </Link>
              ) : (
                <Link
                  to="/account"
                  onClick={() => setOpen(false)}
                  className="flex items-center justify-center rounded-lg bg-[#0a2336] px-3 py-3 text-sm font-semibold text-white transition hover:bg-[#f97316]"
                >
                  My Account
                </Link>
              )}
            </nav>

            <div className="space-y-3">
              {categories.map((cat) => (
                <div
                  key={cat.id}
                  className="overflow-hidden rounded-xl border border-gray-200 bg-white"
                >
                  <div
                    className="flex cursor-pointer items-center justify-between bg-white px-4 py-3 transition active:bg-gray-100"
                    onClick={() =>
                      setMobileOpenMenu(
                        mobileOpenMenu === cat.id ? null : cat.id
                      )
                    }
                  >
                    <span className="text-sm font-semibold text-[#2b2b2b]">
                      {cat.category_name}
                    </span>

                    <span className="text-sm font-bold text-[#2b2b2b]">
                      {mobileOpenMenu === cat.id ? "▼" : "▶"}
                    </span>
                  </div>

                  {mobileOpenMenu === cat.id && (
                    <div className="grid grid-cols-2 gap-3 bg-[#f8fafc] px-3 py-3">
                      {products
                        .filter((p) => p.category_id === cat.id)
                        .slice(0, 6)
                        .map((product) => (
                          <Link
                            key={product.id}
                            to={`/product/${cat.category_name.replace(
                              /\s+/g,
                              "-"
                            )}/${cat.id}/${product.name.replace(
                              /\s+/g,
                              "-"
                            )}/${product.id}`}
                            className="group/card block overflow-hidden rounded-[14px] border border-[#d9d9d9] bg-[#f3f3f3] px-2 pb-3 pt-3 text-center shadow-[0_4px_14px_rgba(0,0,0,0.14)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_8px_22px_rgba(0,0,0,0.18)]"
                            onClick={() => setOpen(false)}
                          >
                            <div className="mx-auto flex h-[92px] w-full items-center justify-center overflow-hidden rounded-[10px] bg-transparent">
                              <img
                                src={`${apiUrl}/storage/${product.image}`}
                                className="max-h-full max-w-full object-contain transition-transform duration-500 ease-out group-hover/card:scale-105"
                                alt={product.name}
                              />
                            </div>

                            <p className="mt-3 line-clamp-2 text-[12px] font-extrabold leading-[16px] text-black">
                              {product.name}
                            </p>

                            <p className="mt-1 text-[11px] font-semibold leading-[16px] text-[#111827]">
                              ${product.discountedPrice}
                            </p>
                          </Link>
                        ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-5 space-y-1 border-t border-gray-100 pt-4">
              <Link
                to="/"
                onClick={() => setOpen(false)}
                className="flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium text-[#2b2b2b] transition hover:bg-[#fff7ed] hover:text-[#f97316]"
              >
                Home
              </Link>

              <Link
                to="/products"
                onClick={() => setOpen(false)}
                className="flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium text-[#2b2b2b] transition hover:bg-[#fff7ed] hover:text-[#f97316]"
              >
                Shop
              </Link>

              <Link
                to="/about-us"
                onClick={() => setOpen(false)}
                className="flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium text-[#2b2b2b] transition hover:bg-[#fff7ed] hover:text-[#f97316]"
              >
                About
              </Link>

              <Link
                to="/contact-us"
                onClick={() => setOpen(false)}
                className="flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium text-[#2b2b2b] transition hover:bg-[#fff7ed] hover:text-[#f97316]"
              >
                Contact
              </Link>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;